import React, { useState, useEffect, useRef, useCallback } from "react";
import { Grid, Card, Divider, CircularProgress, Autocomplete, Chip, LinearProgress, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDPeriodo from "components/MDPeriodo";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import { postNoResponse, post, get } from "utils/WebService";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import MigracaoDeParaDominios from "./MigracaoDeParaDominios";
import HonorariosEmMaisDeUmCliente from "./HonorariosEmMaisDeUmCliente";

const SITUACOES = {
    EM_ANDAMENTO: { rotulo: "Em andamento", cor: "info" },
    CONCLUIDA: { rotulo: "Concluída", cor: "success" },
    ERRO: { rotulo: "Erro", cor: "error" },
};

const duracaoTexto = (segundos) => {
    if (segundos == null || segundos < 0) return "-";
    const h = Math.floor(segundos / 3600);
    const m = Math.floor((segundos % 3600) / 60);
    const s = Math.floor(segundos % 60);
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
    if (m > 0) return `${m}min ${String(s).padStart(2, "0")}s`;
    return `${s}s`;
};

const numero = (n) => Number(n || 0).toLocaleString("pt-BR");
const hora = (data) => (data ? new Date(data).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-");

export default function MigracaoLegado() {
    const [loading, setLoading] = useState(false);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");
    const [idCliente, setIdCliente] = useState("");
    const [pkDevedor, setPkDevedor] = useState("");
    const [clienteDestinoRal, setClienteDestinoRal] = useState("");
    const [clientesRal, setClientesRal] = useState([]);
    const [deParaClientes, setDeParaClientes] = useState({});
    const [salvandoVinculo, setSalvandoVinculo] = useState(false);
    const [dataInicio, setDataInicio] = useState("");
    const [dataFim, setDataFim] = useState("");
    const [minSeqLigacao, setMinSeqLigacao] = useState("");
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);

    // Andamento das etapas (GET /migracao/progresso): a cada 2 s com alguma rodando, senão a cada 15 s
    const [progresso, setProgresso] = useState(null);
    const [etapaLog, setEtapaLog] = useState(null);
    const etapaLogRef = useRef(null);
    const logRef = useRef(null);

    const carregarProgresso = useCallback(async () => {
        try {
            const etapa = etapaLogRef.current;
            const res = await get(`/migracao/progresso${etapa ? `?etapa=${encodeURIComponent(etapa)}` : ""}`);
            if (res) setProgresso(res);
        } catch (e) {
            // sem andamento (backend fora do ar): tenta de novo na próxima rodada
        }
    }, []);

    useEffect(() => {
        carregarClientesRalEDePara();
        carregarProgresso();
    }, []);

    const emAndamento = Boolean(progresso?.emAndamento);
    useEffect(() => {
        const id = setInterval(carregarProgresso, emAndamento ? 2000 : 15000);
        return () => clearInterval(id);
    }, [emAndamento, carregarProgresso]);

    const escolherEtapaLog = (etapa) => {
        etapaLogRef.current = etapa;
        setEtapaLog(etapa);
        carregarProgresso();
    };

    // O log acompanha a última linha, a não ser que a pessoa tenha subido para ler
    useEffect(() => {
        const el = logRef.current;
        if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 60) el.scrollTop = el.scrollHeight;
    }, [progresso?.mensagens]);

    const execucoes = progresso?.execucoes || [];
    const execucaoDe = (etapa) => execucoes.find(e => e.etapa === etapa);

    const carregarClientesRalEDePara = async () => {
        try {
            const [resRal, resMappings] = await Promise.all([
                get('/migracao/clientes-ral').catch(() => []),
                get('/migracao/de-para-dominios').catch(() => ({}))
            ]);
            const ralArray = Array.isArray(resRal) ? resRal : (resRal?.list || resRal?.data || []);
            setClientesRal(ralArray);
            if (resMappings?.cliente) {
                setDeParaClientes(resMappings.cliente);
            }
        } catch (e) {
            console.error("Erro ao carregar clientes RAL ou de-para:", e);
        }
    };

    useEffect(() => {
        const idTrim = idCliente.trim();
        if (idTrim && deParaClientes[idTrim]) {
            setClienteDestinoRal(String(deParaClientes[idTrim]));
        } else {
            setClienteDestinoRal("");
        }
    }, [idCliente, deParaClientes]);

    const showSnackbar = (msg, isSuccess) => {
        setSbMessage(msg);
        if (isSuccess) setSuccessSB(true);
        else setErrorSB(true);
    };

    const handleSalvarVinculoCliente = async () => {
        if (!idCliente.trim()) {
            showSnackbar("Informe o ID do Cliente no Legado primeiro!", false);
            return;
        }
        setSalvandoVinculo(true);
        try {
            await post('/migracao/salvar-de-para-cliente', {
                idClienteLegado: idCliente.trim(),
                idPessoaRal: clienteDestinoRal || ""
            });
            setDeParaClientes(prev => ({
                ...prev,
                [idCliente.trim()]: clienteDestinoRal || ""
            }));
            if (clienteDestinoRal) {
                const cEncontrado = clientesRal.find(c => String(c.id) === String(clienteDestinoRal));
                const cNome = cEncontrado?.nome || clienteDestinoRal;
                showSnackbar(`Sucesso: Fichas do Cliente Legado ${idCliente} serão associadas ao Cliente RAL ${cNome}!`, true);
            } else {
                showSnackbar(`Vínculo do Cliente Legado ${idCliente} removido. Ao migrar, um novo cliente será criado se não existir por documento.`, true);
            }
        } catch (e) {
            showSnackbar(`Erro ao salvar vínculo de cliente: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setSalvandoVinculo(false);
        }
    };

    // A migração usa só o vínculo salvo (De-Para de Cliente). Cliente Destino escolhido e não salvo: salva antes de executar,
    // senão as fichas ficariam sem cliente (hmg, 27/10/2026, cliente 40). Devolve false se a pessoa desistir ou o salvar falhar.
    const garantirVinculoSalvo = async () => {
        const idTrim = idCliente.trim();
        if (!idTrim) return true;
        const salvo = deParaClientes[idTrim] ? String(deParaClientes[idTrim]) : "";
        const naTela = clienteDestinoRal ? String(clienteDestinoRal) : "";
        if (salvo === naTela) return true;
        const nomeDe = (id) => clientesRal.find(c => String(c.id) === id)?.nome || id;
        const pergunta = naTela
            ? `O Cliente Destino no RAL escolhido (${naTela} - ${nomeDe(naTela)}) ainda não foi salvo para o cliente ${idTrim} do legado${salvo ? `, que está ligado a ${salvo} - ${nomeDe(salvo)}` : ""}. A migração usa só o vínculo salvo.\n\nSalvar o vínculo agora e continuar?`
            : `O Cliente Destino foi apagado na tela, mas o cliente ${idTrim} do legado continua ligado a ${salvo} - ${nomeDe(salvo)}.\n\nTirar o vínculo agora e continuar?`;
        if (!window.confirm(pergunta)) return false;
        try {
            await post('/migracao/salvar-de-para-cliente', { idClienteLegado: idTrim, idPessoaRal: naTela });
            setDeParaClientes(prev => ({ ...prev, [idTrim]: naTela }));
            return true;
        } catch (e) {
            showSnackbar(`Erro ao salvar o vínculo do cliente: ${e.message || 'Erro desconhecido'}`, false);
            return false;
        }
    };

    // jaConfirmado: a etapa já perguntou com o que vai fazer (ex.: 4.8, depois do diagnóstico)
    const runMigration = async (endpoint, name, extraPayload = {}, jaConfirmado = false) => {
        if (!(await garantirVinculoSalvo())) return;
        const etapa = endpoint.split("/").pop();
        const rodando = execucoes.filter(e => e.situacao === "EM_ANDAMENTO" && e.etapa !== etapa);
        const aviso = rodando.length
            ? `Ainda está em andamento: ${rodando.map(e => e.nome).join(", ")}.\n\nDeseja iniciar a migração de ${name} mesmo assim?`
            : `Deseja iniciar a migração de ${name}?`;
        if ((!jaConfirmado || rodando.length) && !window.confirm(aviso)) return;
        setLoading(true);
        try {
            const payload = {
                ...(idCliente.trim() ? { idClienteLegado: idCliente.trim() } : {}),
                ...(pkDevedor.trim() ? { pkDevedor: pkDevedor.trim() } : {}),
                ...extraPayload
            };
            await postNoResponse(endpoint, payload);
            showSnackbar(`Migração de ${name} iniciada. Acompanhe o andamento na própria etapa e no painel Andamento.`, true);
            escolherEtapaLog(etapa);
        } catch (e) {
            showSnackbar(`Erro ao iniciar ${name}: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setLoading(false);
        }
    };

    // 4.8: primeiro o diagnóstico (só leitura, o backend compara com o TMK); a confirmação mostra o que muda e pergunta à
    // parte se apaga os lançamentos e as ligações a títulos que não são dos acordos
    const handlePagamentosAcordos = async () => {
        if (!idCliente.trim()) {
            showSnackbar("Informe o ID do Cliente no Legado: a etapa corrige os acordos migrados de um cliente.", false);
            return;
        }
        setLoading(true);
        let previa;
        try {
            previa = await get(`/migracao/diagnostico-pagamentos-acordos?idClienteLegado=${encodeURIComponent(idCliente.trim())}`);
        } catch (e) {
            setLoading(false);
            showSnackbar(`Não deu para conferir os pagamentos: ${e.message || 'Erro desconhecido'}`, false);
            return;
        }
        setLoading(false);
        const linhas = Object.entries(previa?.contagem || {}).filter(([, n]) => n > 0).map(([nome, n]) => `- ${nome}: ${numero(n)}`).join("\n");
        const gravar = (previa?.corrigir || 0) + (previa?.incluir || 0) + (previa?.ligarTitulos || 0) + (previa?.valorNegociado || 0);
        const aApagar = (previa?.apagarLancamentos || 0) + (previa?.desligarTitulos || 0);
        if (!gravar && !aApagar) {
            window.alert(`Nada a corrigir nos acordos migrados do cliente ${idCliente.trim()}.\n\n${linhas}`);
            return;
        }
        if (!window.confirm(`Pagamentos e títulos dos acordos migrados do cliente ${idCliente.trim()}:\n\n${linhas}\n\nCorrigir agora? `
            + `Corrige ${numero(previa.corrigir)} pagamento(s), inclui ${numero(previa.incluir)}, liga ${numero(previa.ligarTitulos)} título(s) `
            + `e acerta o saldo no acordo de ${numero(previa.valorNegociado)} título(s).`)) {
            return;
        }
        const apagar = aApagar > 0 && window.confirm(`Apagar também ${numero(previa.apagarLancamentos)} lançamento(s) que não são das parcelas `
            + `e ${numero(previa.desligarTitulos)} ligação(ões) de acordos a títulos que não são deles? Não dá para desfazer.\n\n`
            + `OK = corrigir e apagar; Cancelar = só corrigir (nada é apagado).`);
        runMigration('/migracao/pagamentos-acordos', 'Pagamentos e Títulos dos Acordos', { apagar: apagar ? "true" : "false" }, true);
    };

    const handleLimparCliente = async () => {
        if (!idCliente.trim()) {
            showSnackbar("Informe o ID do Cliente no Legado para efetuar a limpeza!", false);
            return;
        }
        // Primeiro a simulação (o backend faz tudo e desfaz): a confirmação já mostra o que vai sair
        setLoading(true);
        let previa;
        try {
            previa = await post('/migracao/limpar-cliente', { idClienteLegado: idCliente.trim(), simular: true });
        } catch (e) {
            setLoading(false);
            showSnackbar(`Não dá para limpar o cliente: ${e.message || 'Erro desconhecido'}`, false);
            return;
        }
        setLoading(false);
        const linhas = Object.entries(previa?.apagados || {}).filter(([, n]) => n > 0).map(([nome, n]) => `- ${nome}: ${n}`).join("\n");
        if (!window.confirm(`ATENÇÃO: Deseja apagar TODOS os dados migrados e o De-Para do Cliente ID ${idCliente}? Essa ação não pode ser desfeita.\n\nVai apagar:\n${linhas || "- nada"}`)) {
            return;
        }
        setLoading(true);
        try {
            const res = await post('/migracao/limpar-cliente', { idClienteLegado: idCliente.trim() });
            showSnackbar(res?.mensagem || `Dados do Cliente ${idCliente} limpos com sucesso!`, true);
            carregarClientesRalEDePara();
        } catch (e) {
            showSnackbar(`Erro ao limpar dados do cliente: ${e.message || 'Erro desconhecido'}`, false);
        } finally {
            setLoading(false);
        }
    };

    // Situação, barra, contagem, tempo e erros da última execução da etapa
    const andamentoEtapa = (etapa) => {
        const ex = execucaoDe(etapa);
        if (!ex) {
            return <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={{ opacity: 0.8 }}>Ainda não executada desde a última subida do sistema.</MDTypography>;
        }
        const sit = SITUACOES[ex.situacao] || SITUACOES.EM_ANDAMENTO;
        const rodando = ex.situacao === "EM_ANDAMENTO";
        const temTotal = ex.total >= 0;
        const pct = temTotal ? (ex.total === 0 ? 100 : Math.min(100, (ex.processados / ex.total) * 100)) : null;
        const restante = rodando && temTotal && ex.processados > 0 && ex.total > ex.processados
            ? ex.duracaoSegundos * (ex.total - ex.processados) / ex.processados : null;
        return (
            <MDBox mt={1}>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                    <Chip size="small" variant="outlined" color={sit.cor} label={sit.rotulo} sx={{ fontWeight: "bold", height: 22 }} />
                    <MDTypography variant="caption" color={rodando && /conexão com o banco caiu/i.test(ex.fase || "") ? "warning" : "text"} fontWeight="medium">
                        {rodando ? ex.fase : `${hora(ex.inicio)} às ${hora(ex.fim)}`}
                        {" · "}{rodando ? `há ${duracaoTexto(ex.duracaoSegundos)}` : `durou ${duracaoTexto(ex.duracaoSegundos)}`}
                        {restante != null && ` · faltam ~${duracaoTexto(restante)}`}
                    </MDTypography>
                    {ex.erros > 0 && (
                        <Tooltip title="Mensagens de erro da etapa: veja no painel Andamento">
                            <MDTypography variant="caption" color="error" fontWeight="bold" sx={{ cursor: "pointer" }} onClick={() => escolherEtapaLog(etapa)}>
                                {numero(ex.erros)} erro(s)
                            </MDTypography>
                        </Tooltip>
                    )}
                </MDBox>
                {temTotal && (
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                        <MDBox flex={1}>
                            <LinearProgress variant="determinate" value={pct} color={ex.situacao === "ERRO" ? "error" : (rodando ? "info" : "success")}
                                sx={{ height: 8, borderRadius: 4, "& .MuiLinearProgress-bar": { borderRadius: 4 } }} />
                        </MDBox>
                        <MDTypography variant="caption" color="text" fontWeight="bold" sx={{ whiteSpace: "nowrap" }}>
                            {numero(ex.processados)} de {numero(ex.total)} ({pct.toFixed(pct < 10 && pct > 0 ? 1 : 0).replace(".", ",")}%)
                        </MDTypography>
                    </MDBox>
                )}
                {!temTotal && rodando && <LinearProgress color="info" sx={{ height: 8, borderRadius: 4 }} />}
                {ex.situacao === "ERRO" && ex.erro && (
                    <MDTypography variant="caption" color="error" display="block" mt={0.5}>Parou com erro: {ex.erro}</MDTypography>
                )}
                {ex.retomadas > 0 && (
                    <MDTypography variant="caption" color="warning" display="block" mt={0.25}>
                        A conexão com o banco caiu {ex.retomadas === 1 ? "1 vez" : `${ex.retomadas} vezes`}; a etapa foi retomada sozinha (o que já tinha migrado foi pulado).
                    </MDTypography>
                )}
                {(ex.filtros || ex.usuario) && (
                    <MDTypography variant="caption" color="text" display="block" mt={0.25} sx={{ opacity: 0.8 }}>
                        {[ex.filtros, ex.usuario ? `por ${ex.usuario}` : null].filter(Boolean).join(" · ")}
                    </MDTypography>
                )}
            </MDBox>
        );
    };

    const cartaoEtapa = (etapa, titulo, descricao, executar, extra = null) => {
        const rodando = execucaoDe(etapa)?.situacao === "EM_ANDAMENTO";
        return (
            <MDBox key={etapa} p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: rodando ? "info.main" : "divider", borderRadius: 2 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                    <MDBox flex={1} minWidth={0}>
                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>{titulo}</MDTypography>
                        <MDTypography variant="caption" color="text">{descricao}</MDTypography>
                        {andamentoEtapa(etapa)}
                    </MDBox>
                    <MDButton variant="gradient" color="info" disabled={loading || rodando} onClick={executar}>
                        {rodando ? "Executando..." : "Executar"}
                    </MDButton>
                </MDBox>
                {extra}
            </MDBox>
        );
    };

    // Últimas mensagens que a etapa escreveu no log do servidor (atualiza sozinho)
    const painelAndamento = () => {
        const mensagens = progresso?.mensagens || [];
        const etapaMostrada = progresso?.etapaMensagens;
        return (
            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                    <MDBox>
                        <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>Andamento</MDTypography>
                        <MDTypography variant="caption" color="text">
                            Mensagens da etapa, as mesmas do log do servidor. {emAndamento ? "Atualiza a cada 2 segundos." : "Atualiza a cada 15 segundos."} Fica só até o backend reiniciar.
                        </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={0.75} flexWrap="wrap">
                        {execucoes.map(e => (
                            <Chip key={e.etapa} size="small" label={e.nome}
                                variant={e.etapa === etapaMostrada ? "filled" : "outlined"}
                                color={(SITUACOES[e.situacao] || SITUACOES.EM_ANDAMENTO).cor}
                                onClick={() => escolherEtapaLog(e.etapa)} sx={{ fontWeight: "bold" }} />
                        ))}
                    </MDBox>
                </MDBox>
                <MDBox ref={logRef} sx={{ maxHeight: 320, overflowY: "auto", p: 1.5, borderRadius: 1, backgroundColor: cores.painelEscuro, border: `1px solid ${cores.borda}`, fontFamily: "Consolas, 'Courier New', monospace" }}>
                    {mensagens.length === 0 ? (
                        <MDTypography variant="caption" color="text">{etapaLog || etapaMostrada ? "Sem mensagens ainda." : "Nenhuma migração executada desde a última subida do sistema."}</MDTypography>
                    ) : mensagens.map((linha, i) => {
                        const erro = linha.startsWith("! ");
                        return (
                            <MDTypography key={i} variant="caption" display="block" color={erro ? "error" : "text"}
                                sx={{ fontFamily: "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
                                {erro ? linha.slice(2) : linha}
                            </MDTypography>
                        );
                    })}
                </MDBox>
            </MDBox>
        );
    };

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card variant="outlined" sx={{ 
                    p: 2, 
                    mb: 2, 
                    borderColor: "divider" 
                }}>
                    <MDBox>
                        <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"}>Migração de Dados (Legado)</MDTypography>
                        <MDTypography variant="button" color="text" mb={2}>
                            Execute os passos abaixo sequencialmente para migrar os dados do banco antigo para o novo.
                        </MDTypography>
                        
                        <MDBox mt={3} mb={3} p={3} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                            <MDTypography variant="h6" fontWeight="medium" color={darkMode ? "white" : "dark"}>Filtros e Associação de Cliente (De-Para)</MDTypography>
                            <MDTypography variant="caption" color="text" display="block" mb={2}>
                                Se preenchido, apenas os dados pertencentes a este Cliente ou PK/Código do Devedor Legado serão migrados. Você também pode associar este cliente legado a outro cliente já existente no RAL.
                            </MDTypography>
                            
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <MDInput 
                                        type="number" 
                                        label="ID do Cliente no Legado" 
                                        fullWidth
                                        value={idCliente} 
                                        onChange={(e) => setIdCliente(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MDInput 
                                        type="text" 
                                        label="PK / Código do Devedor (Legado)" 
                                        fullWidth
                                        value={pkDevedor} 
                                        onChange={(e) => setPkDevedor(e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <MDButton 
                                        variant="gradient" 
                                        color="error" 
                                        disabled={loading || !idCliente.trim()} 
                                        onClick={handleLimparCliente}
                                        fullWidth
                                    >
                                        Limpar Dados Migrados do Cliente
                                    </MDButton>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} alignItems="center" mt={0.5}>
                                <Grid item xs={12} md={8}>
                                    <Autocomplete
                                        options={clientesRal}
                                        getOptionLabel={(option) => `${option.id} - ${option.nome || ''} ${option.documento ? '(' + option.documento + ')' : ''}`}
                                        value={clientesRal.find(c => String(c.id) === String(clienteDestinoRal)) || null}
                                        onChange={(e, newVal) => setClienteDestinoRal(newVal ? String(newVal.id) : "")}
                                        renderInput={(params) => (
                                            <MDInput 
                                                {...params} 
                                                label="Cliente Destino no RAL (De-Para / Redirecionar Carteira)" 
                                                placeholder="Selecione o cliente no RAL para onde os devedores devem migrar..." 
                                            />
                                        )}
                                        fullWidth
                                    />
                                    {idCliente.trim() && deParaClientes[idCliente.trim()] ? (
                                        <MDTypography variant="caption" color="success" fontWeight="medium" display="block" mt={0.5}>
                                            ✓ Mapeado no De-Para para o Cliente ID {deParaClientes[idCliente.trim()]} ({clientesRal.find(c => String(c.id) === String(deParaClientes[idCliente.trim()]))?.nome || 'Cliente RAL'})
                                        </MDTypography>
                                    ) : (
                                        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                            Opcional. Permite redirecionar todos os devedores deste cliente legado para outro cliente já cadastrado no RAL.
                                        </MDTypography>
                                    )}
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <MDButton 
                                        variant="gradient" 
                                        color="info" 
                                        disabled={loading || salvandoVinculo || !idCliente.trim()} 
                                        onClick={handleSalvarVinculoCliente}
                                        fullWidth
                                    >
                                        {salvandoVinculo ? <CircularProgress size={20} color="white" /> : "Salvar De-Para de Cliente"}
                                    </MDButton>
                                </Grid>
                            </Grid>
                        </MDBox>

                        <Divider sx={{ my: 3 }} />
                        
                        {loading && (
                            <MDBox display="flex" flexDirection="column" alignItems="center" my={3}>
                                <CircularProgress color="info" />
                                <MDTypography variant="caption" mt={1}>Enviando...</MDTypography>
                            </MDBox>
                        )}

                        <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
                            {cartaoEtapa("usuarios", "0. Migrar Usuários (Cobradores)", "Importa os usuários/operadores do legado.",
                                () => runMigration('/migracao/usuarios', 'Usuários (Cobradores)'))}
                            {cartaoEtapa("tipos-ocorrencia", "0.5. Migrar Tipos de Ocorrência", "Importa o cadastro de tipos de ocorrências.",
                                () => runMigration('/migracao/tipos-ocorrencia', 'Tipos de Ocorrência'))}
                            {cartaoEtapa("tipos-acao", "0.6. Migrar Tipos de Ação (Judicial)", "Importa os tipos de ações da tabela do legado para a tabela de ações (tbAcao).",
                                () => runMigration('/migracao/tipos-acao', 'Tipos de Ação'))}
                            {cartaoEtapa("clientes", "1. Migrar Clientes (Credores)", "Importa os dados básicos dos credores (TbPessoa).",
                                () => runMigration('/migracao/clientes', 'Clientes'))}

                            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <MigracaoDeParaDominios showSnackbar={showSnackbar} idCliente={idCliente} />
                            </MDBox>

                            {cartaoEtapa("fichas", "2. Migrar Fichas e Devedores", "Cria as Fichas (TbDevedorCarteira) e os Devedores (TbDevedor).",
                                () => runMigration('/migracao/fichas', 'Fichas'))}
                            {cartaoEtapa("marcacoes-legado", "2.5. Marcações do Legado (Nomeref1/Nomeref2)", "Coloca nas fichas já migradas as marcações especiais dos campos Nomeref1 e Nomeref2 do TMK, conforme o De-Para de Domínios > Marcações (sem escolha, cria com o mesmo nome). A etapa Fichas também faz isso no fim. Só acrescenta; pode rodar de novo.",
                                () => runMigration('/migracao/marcacoes-legado', 'Marcações do Legado'))}
                            {cartaoEtapa("contratos", "3. Migrar Contratos e Dívidas", "Importa os débitos associando-os às fichas geradas no passo anterior.",
                                () => runMigration('/migracao/contratos', 'Contratos/Dívidas'))}
                            {cartaoEtapa("acordos", "4. Migrar Acordos e Lançamentos", "Importa acordos firmados e pagamentos (lançamentos).",
                                () => runMigration('/migracao/acordos', 'Acordos e Lançamentos'))}
                            {cartaoEtapa("exito-acordos", "4.5. % de Êxito dos Acordos (RG_IE)", "Grava só o % de êxito do TMK (campo RG_IE) nos acordos já migrados, sem refazer parcelas nem pagamentos. O % de honorários da campanha não é êxito: o acordo que o recebeu como êxito fica sem. Usado nos relatórios. Pode rodar de novo.",
                                () => runMigration('/migracao/exito-acordos', '% de Êxito dos Acordos'))}
                            {cartaoEtapa("por-conta", "4.6. Valor Pago dos Contratos", "Preenche o Valor Pago dos contratos com o que o sistema já tem, sem ler o TMK: o principal já pago = valor original - saldo - descontos das baixas (no quitado, o valor original). Só no contrato com saldo ou Liquidado; o saldo zero que não é pagamento (devolvido, cancelado) fica de fora. Com o Cliente preenchido, os contratos migrados dele; vazio, todos os contratos do sistema. Não mexe no saldo, no valor lançado à mão nem no zerado pela importação. Rodar depois da etapa 4 (Acordos), que marca os quitados. Pode rodar de novo.",
                                () => runMigration('/migracao/por-conta', 'Valor Pago dos Contratos'))}
                            {cartaoEtapa("campos-cliente", "4.7. Campos do Cliente e Nº do Acordo no TMK", "Para a Prestação de Contas da UMC: grava nos registros já migrados do cliente o segmento e a unidade do devedor (Obs1 e Empresa do TMK, só no cliente com o modelo de PC da UMC em Ferramentas > Clientes), o mês/ano de referência dos títulos (Outros2) e o nº do acordo no TMK. Exige o Cliente. Não refaz nada e o vazio no TMK não apaga. Pode rodar de novo.",
                                () => runMigration('/migracao/campos-cliente', 'Campos do Cliente e Nº do Acordo no TMK'))}
                            {cartaoEtapa("pagamentos-acordos", "4.8. Pagamentos e Títulos dos Acordos", "Corrige os acordos já migrados do cliente sem refazer nada: o pagamento de cada parcela passa a ser o recibo inteiro do TMK (antes, o pagamento que quitou mais de um título entrava só com uma parte) e com a forma de pagamento certa (antes os boletos apareciam como Ordem de Pagamento); tira o pagamento que foi parar na parcela de outro acordo; e deixa cada acordo ligado só aos títulos dele (o ativo, os do AcordosCDC; o cancelado, os que os pagamentos dele pagaram). Mostra antes o que vai mudar e pergunta à parte se apaga. A baixa feita no sistema não muda. Exige o Cliente. Pode rodar de novo.",
                                handlePagamentosAcordos)}
                            {cartaoEtapa("boletos", "5. Migrar Boletos Físicos", "Importa os boletos emitidos na conta bancária do cliente e leva o Último Nosso Número de cada conta até o maior do legado (nunca diminui).",
                                () => runMigration('/migracao/boletos', 'Boletos'))}
                            {cartaoEtapa("ligacoes", "6. Migrar Histórico de Ligações", "Importa o histórico de cobrança e gravações telefônicas.",
                                () => runMigration('/migracao/ligacoes', 'Histórico de Ligações', { dataInicio, dataFim, minSeqLigacao }),
                                <Grid container spacing={2} mt={0.5}>
                                    <Grid item xs={12} md={8}>
                                        <MDPeriodo inicio={dataInicio} fim={dataFim}
                                            onChange={({ inicio, fim }) => { setDataInicio(inicio); setDataFim(fim); }} />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <MDInput type="number" label="A partir do SeqLigação" fullWidth value={minSeqLigacao} onChange={(e) => setMinSeqLigacao(e.target.value)} />
                                    </Grid>
                                </Grid>)}
                            {cartaoEtapa("processos", "7. Migrar Processos Ajuizados (BVJuridico)", "Importa os processos ajuizados do BVJuridico amarrando diretamente à ficha do devedor.",
                                () => runMigration('/migracao/processos', 'Processos Ajuizados (BVJuridico)'))}

                            <MDBox p={2} sx={{ backgroundColor: darkMode ? "background.default" : "#fff", border: '1px solid', borderColor: "divider", borderRadius: 2 }}>
                                <HonorariosEmMaisDeUmCliente showSnackbar={showSnackbar} />
                            </MDBox>

                            {painelAndamento()}
                        </MDBox>
                    </MDBox>
                </Card>
            </Grid>
            
            <MDSnackbar
                color="success"
                icon="check"
                title="Sucesso"
                content={sbMessage}
                open={successSB}
                onClose={() => setSuccessSB(false)}
                close={() => setSuccessSB(false)}
                bgWhite={!darkMode}
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Erro"
                content={sbMessage}
                open={errorSB}
                onClose={() => setErrorSB(false)}
                close={() => setErrorSB(false)}
                bgWhite={!darkMode}
            />
        </Grid>
    );
}
